import React, { type ForwardedRef, forwardRef, MouseEventHandler, ReactElement, Ref } from 'react';
import type { CSSProperties } from 'fela';
import { useFelaEnhanced } from '@ackee/fela';
import type { RulesExtend } from '@ackee/fela';

import * as felaRules from './DynamicContainer.rules';
import { ContainerType } from '../constants';

interface DynamicContainerProps {
    children: React.ReactNode;
    as?:
        | ContainerType.ARTICLE
        | ContainerType.DIV
        | ContainerType.FOOTER
        | ContainerType.HEADER
        | ContainerType.NAV
        | ContainerType.SECTION;
    onMouseEnter?: MouseEventHandler<HTMLElement>;
    onMouseLeave?: MouseEventHandler<HTMLElement>;
    id?: string;
    eventId?: string;
    extend?: RulesExtend<typeof felaRules>;
    ref?: Ref<HTMLElement>;
}

export interface DynamicContainerStyleProps {
    columns?: number;
    justify?: CSSProperties['justifySelf'];
    type?: ContainerType.BLOCK | ContainerType.FLEX | ContainerType.GRID | ContainerType.INLINE_BLOCK;
    isFluid?: boolean;
    startAt?: string;
    xsColumns?: number;
}

export const DynamicContainer = forwardRef(
    (
        {
            children,
            extend,
            as = ContainerType.DIV,
            onMouseEnter,
            onMouseLeave,
            id,
            eventId,
            columns = 12,
            xsColumns = 12,
            justify = 'auto',
            type,
            isFluid = false,
            startAt,
            ...props
        }: DynamicContainerProps & DynamicContainerStyleProps,
        ref: ForwardedRef<HTMLElement>,
    ) => {
        const { styles } = useFelaEnhanced(felaRules, {
            extend,
            columns,
            justify,
            type,
            isFluid,
            startAt,
            xsColumns,
            ...props,
        });
        return React.createElement(
            as,
            {
                className: styles.container,
                ref,
                onMouseEnter,
                onMouseLeave,
                id,
                'data-event-id': eventId,
            },
            children,
        );
    },
) as <PassProps = {}>(props: DynamicContainerProps & DynamicContainerStyleProps & PassProps) => ReactElement;
